import { Controller } from "@hotwired/stimulus"
import { z } from "zod"

const CookingInfoSchema = z.object({
  prep_time: z.string(),
  cook_time: z.string(),
  total_time: z.string(),
  yield: z.number(),
  calories: z.number(),
  protein: z.number(),
  carbs: z.number(),
  fat: z.number(),
  fiber: z.number(),
  sugars: z.number(),
  sodium: z.number()
})

const RecipeSchema = z.object({
  name: z.string(),
  cooking_info: CookingInfoSchema,
  ingredients: z.array(
    z.object({
      name: z.string(),
      ingredients: z.array(z.object({ name: z.string() }))
    })
  ),
  instructions: z.array(
    z.object({
      name: z.string(),
      steps: z.array(z.object({ step: z.string() }))
    })
  ),
  links: z.array(
    z.object({
      url: z.string(),
      title: z.string().optional()
    })
  )
})

export default class extends Controller {
  static targets = ["input", "status", "submit"]

  connect() {
    this.validate()
  }

  validate() {
    const text = this.inputTarget.value.trim()
    if (!text) {
      this.clear()
      return
    }

    let payload
    try {
      payload = JSON.parse(text)
    } catch (e) {
      this.fail([`Invalid JSON: ${e.message}`])
      return
    }

    const result = RecipeSchema.safeParse(payload)
    if (!result.success) {
      this.fail(result.error.issues.map(formatIssue))
      return
    }

    this.succeed(result.data.name)
  }

  clear() {
    this.statusTarget.innerHTML = ""
    this.submitTarget.disabled = true
  }

  succeed(name) {
    const label = name && name.trim() ? name : "(untitled)"
    this.statusTarget.innerHTML =
      `<div class="text-success font-medium">✓ Ready to import: ${escapeHtml(label)}</div>`
    this.submitTarget.disabled = false
  }

  fail(errors) {
    const items = errors.map((e) => `<li>${escapeHtml(e)}</li>`).join("")
    this.statusTarget.innerHTML =
      `<ul class="text-danger list-disc list-inside space-y-1">${items}</ul>`
    this.submitTarget.disabled = true
  }
}

function formatIssue(issue) {
  return `${formatPath(issue.path) || "(root)"}: ${issue.message}`
}

function formatPath(path) {
  return path.reduce((acc, seg) => {
    if (typeof seg === "number") return `${acc}[${seg}]`
    return acc ? `${acc}.${seg}` : String(seg)
  }, "")
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;"
  }[c]))
}
